import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Drawer, Form, Input, Select, message } from 'antd';
import axios from 'axios';
import { LoginContext } from '../../context/loginContext';

const { Option } = Select;

const CArticulos = () => {
    const [articulos, setArticulos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editArticulo, setEditArticulo] = useState(null);
    const [form] = Form.useForm();
    const { user } = useContext(LoginContext);

    // Estados de búsqueda para cada columna
    const [searchText, setSearchText] = useState({
        nombre: '',
        categoria: '',
        marca: '',
        unidadDeMedida: ''
    });

    // Obtener artículos, categorías y marcas al cargar el componente
    useEffect(() => {
        fetchArticulos();
        fetchCategorias();
        fetchMarcas();
    }, []);

    const fetchArticulos = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/articulos`, {
                headers: {
                    Authorization: `Bearer ${user.token}`, // Enviar el token en la cabecera
                },
            });
            setArticulos(response.data);
        } catch (error) {
            console.error('Error al obtener artículos:', error);
        }
    };

    const fetchCategorias = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/categorias`, {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            });
            setCategorias(response.data);
        } catch (error) {
            console.error('Error al obtener categorías:', error);
        }
    };

    const fetchMarcas = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/marcas`, {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            });
            setMarcas(response.data);
        } catch (error) {
            console.error('Error al obtener marcas:', error);
        }
    };

    const openDrawer = (articulo = null) => {
        setEditArticulo(articulo);
        setDrawerVisible(true);
        if (articulo) {
            form.setFieldsValue({
                nombre: articulo.nombre,
                categoria: articulo.idcategoria._id, // Asignar el ID de la categoría
                marca: articulo.idmarca._id, // Asignar el ID de la marca
                tipoUnidad: articulo.unidadDeMedida
            });
        } else {
            form.resetFields(); // Limpiar formulario si es nuevo artículo
        }
    };

    const closeDrawer = () => {
        setDrawerVisible(false);
    };

    const handleSaveArticulo = async (values) => {
        try {
            const dataToSend = {
                nombre: values.nombre,
                idcategoria: values.categoria, // Guardar usando el ID de la categoría
                idmarca: values.marca, // Guardar usando el ID de la marca
                unidadDeMedida: values.tipoUnidad
            };
            const headers = {
                Authorization: `Bearer ${user.token}`,
                "Content-Type": 'application/json'
            };

            if (editArticulo) {
                await axios.put(`${process.env.REACT_APP_API_URL}/articulos/${editArticulo._id}`, dataToSend, { headers });
                message.success('Artículo actualizado correctamente');
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/articulos`, dataToSend, { headers });
                message.success('Artículo creado correctamente');
            }

            fetchArticulos(); // Recargar lista de artículos
            closeDrawer();
        } catch (error) {
            message.error('Error al guardar artículo');
            console.error('Error al guardar artículo:', error);
        }
    };

    // Función de búsqueda por columna
    const handleSearch = (selectedColumn, value) => {
        setSearchText({
            ...searchText,
            [selectedColumn]: value
        });
    };

    // Filtrar los artículos en función de los valores de búsqueda
    const filteredArticulos = articulos.filter((articulo) => {
        return (
            articulo.nombre.toLowerCase().includes(searchText.nombre.toLowerCase()) &&
            articulo.idcategoria.nombre.toLowerCase().includes(searchText.categoria.toLowerCase()) &&
            articulo.idmarca.nombre.toLowerCase().includes(searchText.marca.toLowerCase()) &&
            articulo.unidadDeMedida.toLowerCase().includes(searchText.unidadDeMedida.toLowerCase())
        );
    });

    // Columnas de la tabla con filtros de búsqueda
    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'nombre',
            key: 'nombre',
            filterDropdown: ({ setSelectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={`Buscar Nombre`}
                        value={searchText.nombre}
                        onChange={(e) => handleSearch('nombre', e.target.value)}
                        onPressEnter={() => confirm()}
                    />
                </div>
            ),
            filterIcon: () => <span>🔍</span>,
        },
        {
            title: 'Categoría',
            dataIndex: 'idcategoria',
            key: 'idcategoria',
            render: (categoria) => categoria.nombre,
            filterDropdown: ({ setSelectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={`Buscar Categoría`}
                        value={searchText.categoria}
                        onChange={(e) => handleSearch('categoria', e.target.value)}
                        onPressEnter={() => confirm()}
                    />
                </div>
            ),
            filterIcon: () => <span>🔍</span>,
        },
        {
            title: 'Marca',
            dataIndex: 'idmarca',
            key: 'idmarca',
            render: (marca) => marca.nombre,
            filterDropdown: ({ setSelectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={`Buscar Marca`}
                        value={searchText.marca}
                        onChange={(e) => handleSearch('marca', e.target.value)}
                        onPressEnter={() => confirm()}
                    />
                </div>
            ),
            filterIcon: () => <span>🔍</span>,
        },
        {
            title: 'Tipo de Unidad',
            dataIndex: 'unidadDeMedida',
            key: 'unidadDeMedida',
            filterDropdown: ({ setSelectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={`Buscar Tipo de Unidad`}
                        value={searchText.unidadDeMedida}
                        onChange={(e) => handleSearch('unidadDeMedida', e.target.value)}
                        onPressEnter={() => confirm()}
                    />
                </div>
            ),
            filterIcon: () => <span>🔍</span>,
        },
        {
            title: 'Acciones',
            key: 'acciones',
            render: (text, record) => (
                <Button type="link" onClick={() => openDrawer(record)}>
                    Editar
                </Button>
            ),
        },
    ];

    return (
        <>
            <Button type="primary" onClick={() => openDrawer()} style={{ marginBottom: 16 }}>
                Agregar Artículo
            </Button>

            <Table columns={columns} dataSource={filteredArticulos} rowKey="_id" />

            {/* Drawer para agregar/editar artículos */}
            <Drawer
                title={editArticulo ? 'Editar Artículo' : 'Agregar Artículo'}
                width={400}
                onClose={closeDrawer}
                visible={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Form form={form} layout="vertical" onFinish={handleSaveArticulo}>
                    <Form.Item
                        name="nombre"
                        label="Nombre de Artículo"
                        rules={[{ required: true, message: 'Por favor ingresa el nombre del artículo' }]}
                    >
                        <Input placeholder="Nombre de Artículo" />
                    </Form.Item>

                    <Form.Item
                        name="categoria"
                        label="Categoría"
                        rules={[{ required: true, message: 'Por favor selecciona una categoría' }]}
                    >
                        <Select placeholder="Selecciona una categoría">
                            {categorias.map((categoria) => (
                                <Option key={categoria._id} value={categoria._id}>
                                    {categoria.nombre}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="marca"
                        label="Marca"
                        rules={[{ required: true, message: 'Por favor selecciona una marca' }]}
                    >
                        <Select placeholder="Selecciona una marca">
                            {marcas.map((marca) => (
                                <Option key={marca._id} value={marca._id}>
                                    {marca.nombre}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="tipoUnidad"
                        label="Tipo de Unidad"
                        rules={[{ required: true, message: 'Por favor selecciona el tipo de unidad' }]}
                    >
                        <Select placeholder="Selecciona el tipo de unidad">
                            <Option value="unidades">Unidades</Option>
                            <Option value="kilogramos">Kilogramos</Option>
                            <Option value="litros">Litros</Option>
                            <Option value="pack">Pack</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {editArticulo ? 'Guardar Cambios' : 'Agregar Artículo'}
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
};

export default CArticulos;
